// Third-party libraries
import 'reflect-metadata';

// App API
import { API_METADATA } from '@app/core/api';
import { ParamMetadata } from '../types';

export function Params(key?: string): ParameterDecorator {
  return (target: any, propertyKey: string | symbol | undefined, parameterIndex: number) => {
    if (!propertyKey) return;

    const existingParams: ParamMetadata[] = Reflect.getOwnMetadata(API_METADATA.PARAMS, target, propertyKey) || [];

    // Validate: If key is provided, check for duplicates
    if (key) {
      const duplicate = existingParams.find(p => p.key === key);
      if (duplicate) {
        throw new Error(
          `@Params('${key}') decorator: Duplicate parameter key '${key}' found on ${String(propertyKey)}. ` +
          `Each path parameter key must be unique.`
        );
      }
    }

    existingParams.push({
      index: parameterIndex,
      type: 'params',
      key: key
    });

    Reflect.defineMetadata(API_METADATA.PARAMS, existingParams, target, propertyKey);
  };
}


export function Body(): ParameterDecorator {
  return (target: any, propertyKey: string | symbol | undefined, parameterIndex: number) => {
    if (!propertyKey) return;

    const paramTypes = Reflect.getMetadata('design:paramtypes', target, propertyKey);

    let resolvedDtoClass: (new () => any) | undefined = undefined;

    if (paramTypes && paramTypes[parameterIndex]) {
      const paramType = paramTypes[parameterIndex];

      const isNativeOrBuiltIn =
        [String, Number, Boolean, Date, Array, Object, FormData].includes(paramType);

      const isClass = paramType &&
        typeof paramType === 'function' &&
        paramType.prototype &&
        !isNativeOrBuiltIn &&
        paramType !== Object;

      if (isClass) {
        resolvedDtoClass = paramType;
      }
      // If it's a primitive/interface, resolvedDtoClass remains undefined (no validation needed)
    } else {
      // Only throw error if paramTypes is completely missing or parameter doesn't exist
      throw new Error(
        `@Body() decorator: Could not determine parameter type at index ${parameterIndex}. ` +
        `Make sure 'emitDecoratorMetadata' is enabled in tsconfig.json and the parameter has a type annotation. ` +
        `Usage: @Body() userData: CreateUserDto | @Body() data: string | @Body() data: UpdateUserDto`
      );
    }

    const existingParams: ParamMetadata[] = Reflect.getOwnMetadata(API_METADATA.BODY, target, propertyKey) || [];

    // Validate: Only one @Body() decorator should be used per method
    if (existingParams.length > 0) {
      throw new Error(
        `@Body() decorator: Multiple @Body() decorators found on ${String(propertyKey)}. ` +
        `Only one @Body() parameter is allowed per method.`
      );
    }

    existingParams.push({
      index: parameterIndex,
      type: 'body',
      dtoClass: resolvedDtoClass
    });

    Reflect.defineMetadata(API_METADATA.BODY, existingParams, target, propertyKey);
  };
}
export function Query(key?: string): ParameterDecorator {
  return (target: any, propertyKey: string | symbol | undefined, parameterIndex: number) => {
    if (!propertyKey) return;

    const existingParams: ParamMetadata[] = Reflect.getOwnMetadata(API_METADATA.QUERY, target, propertyKey) || [];

    // Validate: If key is provided, check for duplicates
    if (key) {
      const duplicate = existingParams.find(p => p.key === key);
      if (duplicate) {
        throw new Error(
          `@Query('${key}') decorator: Duplicate query parameter key '${key}' found on ${String(propertyKey)}. ` +
          `Each query parameter key must be unique.`
        );
      }
    }

    existingParams.push({
      index: parameterIndex,
      type: 'query',
      key: key
    });

    Reflect.defineMetadata(API_METADATA.QUERY, existingParams, target, propertyKey);
  };
}

